import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { rolesApi, type RoleListItem } from '@/api/rolesApi'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead, RowActions } from '@/components/table'
import { usersApi } from '@/api/usersApi'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { RolePermissionMatrix } from '@/components/admin/RolePermissionMatrix'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// Per-user column layout. Role and the actions cell are locked — see
// src/components/table/index.ts for the pattern.
const ROLES_TABLE_ID = 'roles'

const ROLES_COLUMNS: TableColumnDef[] = [
  { id: 'role', header: 'Role', locked: true, defaultWidth: 240, sortable: true },
  { id: 'slug', header: 'Slug', defaultWidth: 200, sortable: true },
  { id: 'permissions', header: 'Permissions', defaultWidth: 180, sortable: true },
  // 2 icon buttons at 40px + 4px gap + px-4 padding.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 120 },
]

// Header sorting (z8nrz7e4h6). Full access grants everything, so it sorts as
// more permissions than any counted role.
const ROLES_SORT: SortAccessors<RoleListItem> = {
  role: r => r.displayName,
  slug: r => r.slug,
  permissions: r => (r.fullAccess ? Number.MAX_SAFE_INTEGER : r.permissionKeys.length),
}

/**
 * Why a role's Actions cell is empty.
 *
 * Both controls are guarded, and the two roles that ship with the platform trip
 * both guards at once: `admin` and `super-admin` are full access (no list of
 * permissions to edit) and system (not deletable). Their cells were therefore
 * blank while the roles below them showed a pencil, which read as rows that had
 * failed to load rather than as rows that are locked on purpose.
 */
function noActionsReason(
  role: RoleListItem,
  canManage: boolean,
  canDelete: boolean,
): string | undefined {
  if (!canManage && !canDelete) return 'You do not have permission to change roles'
  if (role.fullAccess && role.isSystem) {
    return 'Built-in full-access role — it already grants everything, and cannot be deleted'
  }
  if (role.fullAccess) return 'Full access — there are no individual permissions to edit'
  if (role.isSystem) return 'Built-in role — it cannot be deleted'
  return undefined
}

export function RolesPage() {
  const cols = useTableColumns(ROLES_TABLE_ID, ROLES_COLUMNS)
  const qc = useQueryClient()
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.rolesManage)
  const canDelete = can(PERMISSIONS.rolesDelete)

  const roles = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list })
  const sortedRoles = useSortedRows(roles.data, cols.sort, ROLES_SORT)
  const catalog = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: usersApi.permissionsCatalog,
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<RoleListItem | null>(null)
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['roles'] })
    void qc.invalidateQueries({ queryKey: ['users'] })
  }

  const createRole = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      setCreateOpen(false)
      setError(null)
      invalidate()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not create role'),
  })

  const setPermissions = useMutation({
    mutationFn: (input: { id: string; keys: string[] }) =>
      rolesApi.setPermissions(input.id, input.keys),
    onSuccess: () => {
      setEditing(null)
      invalidate()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save permissions'),
  })

  const deleteRole = useMutation({
    mutationFn: rolesApi.remove,
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not delete role'),
  })

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-sm text-muted-foreground'>
          Define roles and the permissions each one grants. Roles are assigned{' '}
          <strong>per tenant</strong>, so the same person can hold different roles in different
          tenants. Platform-wide access is the separate super-admin flag on a user.
        </p>
        {/* Grouped so the row keeps its two-part justify-between layout with the
            blurb, rather than spreading three children across it. ColumnsMenu is
            outside the canManage guard: choosing which columns to look at is a
            view preference, not a role-management action. */}
        <div className='flex shrink-0 items-center gap-2'>
          {canManage && (
            <Button onClick={() => setCreateOpen(true)} className='gap-2'>
              <Plus className='h-4 w-4' /> New role
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className='text-sm text-destructive' role='alert'>
          {error}
        </p>
      )}

      <Card>
        <CardContent className='p-0'>
          {roles.isLoading && <p className='p-4 text-sm text-muted-foreground'>Loading…</p>}
          {roles.isError && (
            <p className='p-4 text-sm text-destructive'>
              Could not load roles. {(roles.error as Error).message}
            </p>
          )}
          {roles.data && (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={def.id === 'actions' ? 'text-right' : undefined}
                      resizable={def.id !== 'actions'}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRoles.map((role) => (
                  <TableRow key={role.id}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        role: (
                          <TableCell className='font-medium'>
                            <span className='flex items-center gap-2'>
                              {role.displayName}
                              {role.isSystem && <Badge variant='secondary'>system</Badge>}
                            </span>
                          </TableCell>
                        ),
                        slug: (
                          <TableCell>
                            <code className='text-xs'>{role.slug}</code>
                          </TableCell>
                        ),
                        permissions: (
                          <TableCell>
                            {role.fullAccess ? (
                              <Badge>full access</Badge>
                            ) : (
                              <span className='text-sm text-muted-foreground'>
                                {role.permissionKeys.length} permission
                                {role.permissionKeys.length === 1 ? '' : 's'}
                              </span>
                            )}
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            <RowActions reason={noActionsReason(role, canManage, canDelete)}>
                              {canManage && !role.fullAccess && (
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title='Edit permissions'
                                  onClick={() => {
                                    setError(null)
                                    setEditing(role)
                                  }}
                                >
                                  <Pencil className='h-4 w-4' />
                                </Button>
                              )}
                              {canDelete && !role.isSystem && (
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title='Delete role'
                                  disabled={deleteRole.isPending}
                                  onClick={() => {
                                    if (window.confirm(`Delete role "${role.displayName}"?`)) {
                                      setError(null)
                                      deleteRole.mutate(role.id)
                                    }
                                  }}
                                >
                                  <Trash2 className='h-4 w-4 text-destructive' />
                                </Button>
                              )}
                            </RowActions>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CreateRoleDialog
          catalog={catalog.data ?? []}
          pending={createRole.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(payload) => createRole.mutate(payload)}
        />
      )}

      {editing && (
        <EditPermissionsDialog
          role={editing}
          catalog={catalog.data ?? []}
          pending={setPermissions.isPending}
          onClose={() => setEditing(null)}
          onSave={(keys) => setPermissions.mutate({ id: editing.id, keys })}
        />
      )}
    </div>
  )
}

function CreateRoleDialog({
  catalog,
  pending,
  onClose,
  onSubmit,
}: {
  catalog: import('@/api/usersApi').PermissionCatalogEntry[]
  pending: boolean
  onClose: () => void
  onSubmit: (payload: { slug: string; displayName: string; fullAccess: boolean; permissionKeys: string[] }) => void
}) {
  const [displayName, setDisplayName] = useState('')
  const [slug, setSlug] = useState('')
  const [fullAccess, setFullAccess] = useState(false)
  const [keys, setKeys] = useState<string[]>([])

  // Suggest a slug from the name until the user edits it directly.
  const suggestedSlug = useMemo(
    () => displayName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    [displayName],
  )
  const [slugTouched, setSlugTouched] = useState(false)
  const effectiveSlug = slugTouched ? slug : suggestedSlug

  function submit(e: FormEvent) {
    e.preventDefault()
    onSubmit({ slug: effectiveSlug, displayName: displayName.trim(), fullAccess, permissionKeys: keys })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>New role</DialogTitle>
          <DialogDescription>Create a custom role and choose its permissions.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='role-name'>Display name</Label>
            <Input id='role-name' required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='role-slug'>Slug</Label>
            <Input
              id='role-slug'
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true)
                setSlug(e.target.value)
              }}
            />
          </div>
          <label className='flex items-center justify-between rounded-lg border px-3 py-2'>
            <span>
              <span className='block text-sm font-medium'>Full access</span>
              <span className='block text-xs text-muted-foreground'>Grants every permission</span>
            </span>
            <Switch checked={fullAccess} onCheckedChange={setFullAccess} />
          </label>
          {!fullAccess && (
            <div className='flex flex-col gap-2'>
              <Label>Permissions</Label>
              <RolePermissionMatrix catalog={catalog} value={keys} onChange={setKeys} />
            </div>
          )}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !displayName.trim() || !effectiveSlug}>
              {pending ? 'Creating…' : 'Create role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditPermissionsDialog({
  role,
  catalog,
  pending,
  onClose,
  onSave,
}: {
  role: RoleListItem
  catalog: import('@/api/usersApi').PermissionCatalogEntry[]
  pending: boolean
  onClose: () => void
  onSave: (keys: string[]) => void
}) {
  const [keys, setKeys] = useState<string[]>(role.permissionKeys)
  useEffect(() => setKeys(role.permissionKeys), [role])

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{role.displayName} — permissions</DialogTitle>
          <DialogDescription>Choose the permissions this role grants.</DialogDescription>
        </DialogHeader>
        <RolePermissionMatrix catalog={catalog} value={keys} onChange={setKeys} />
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending} onClick={() => onSave(keys)}>
            {pending ? 'Saving…' : 'Save permissions'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
